// Account-creation / availability / email-exists API for the sign-up flow.
//
// Account creation, email-exists, and the geo availability count are LIVE against the
// anonymous public lane via @twg/api-client's createPublicClient (same-origin cookie, no
// Bearer — the visitor has no token yet; the account's sign_in Set-Cookie sticks). See ADR
// 0025 (public-lane amendment). The availability endpoint is geo-based, so the address step
// threads the picked coordinates (BAN today; LocationIQ later — the threading is the same).
import { ref } from 'vue'
import {
  createPublicClient,
  type RegistrationPayload,
  type RegistrationResponse,
} from '@twg/api-client'

// Never-throwing result contract shared by every call below: callers branch on `ok`.
export interface HttpResult<T = unknown> {
  ok: boolean
  status: number
  data: T | null
  error: string | null
}

export interface AccountResult {
  userId: string
  redirectUrl: string | null
}
export interface AvailabilityResult {
  count: number
}
export interface EmailExistsResult {
  exists: boolean
}

// Pull the plain-text message out of the 422 error serializer ({ error, errors }),
// falling back to a generic label. Never throws.
function messageFrom(err: unknown): string {
  const body = (err as { response?: { data?: { error?: unknown } } })?.response?.data
  if (body && typeof body.error === 'string') return body.error
  return 'Une erreur est survenue. Merci de réessayer.'
}

function toAccountResult(data: RegistrationResponse): AccountResult {
  return { userId: String(data.userId ?? data.id), redirectUrl: data.redirectUrl ?? null }
}

export function useSignupApi() {
  const loading = ref(false)
  const error = ref<string | null>(null)
  const client = createPublicClient()

  // Runs a live client call behind the shared loading/error refs and resolves the
  // { ok, data, error } envelope (never throws — matches the composable's contract).
  async function envelope<T>(run: () => Promise<T>): Promise<HttpResult<T>> {
    loading.value = true
    error.value = null
    try {
      const data = await run()
      return { ok: true, status: 200, data, error: null }
    } catch (err) {
      const message = messageFrom(err)
      error.value = message
      // 0 only when there was no HTTP response at all (network failure), like fetch would report.
      const status = (err as { response?: { status?: number } })?.response?.status ?? 0
      return { ok: false, status, data: null, error: message }
    } finally {
      loading.value = false
    }
  }

  // Both person + org paths hit the same POST /api/v1/public/registration; the backend
  // (SignupChatbotForm) translates the chatbot vocabulary and builds the right STI role.
  // Flexible pass-through: the caller forwards chatbot-shaped answers; the backend
  // (SignupChatbotForm) is the real validator, so we cast to the client's payload type
  // at this boundary rather than re-type every call site.
  function createAccount(payload: Record<string, unknown>): Promise<HttpResult<AccountResult>> {
    return envelope(() =>
      client.createRegistration(payload as RegistrationPayload).then(toAccountResult),
    )
  }

  function createOrganisationAccount(
    payload: Record<string, unknown>,
  ): Promise<HttpResult<AccountResult>> {
    return envelope(() =>
      client.createRegistration(payload as RegistrationPayload).then(toAccountResult),
    )
  }

  // Live geo-based social-proof count (reuses the marketplace search server-side). Missing
  // coordinates → the backend returns 0, so the caller (confirmAddress) only calls this once
  // the picked address has coordinates. `category` defaults to child (Nounou-Top).
  function fetchAvailability(params: {
    role: string
    geoLatitude: number
    geoLongitude: number
    category?: string
  }): Promise<HttpResult<AvailabilityResult>> {
    return envelope(() =>
      client.availability({
        role: params.role,
        geo_latitude: params.geoLatitude,
        geo_longitude: params.geoLongitude,
        category: params.category ?? 'child',
      }),
    )
  }

  function checkEmailExists(email: string): Promise<HttpResult<EmailExistsResult>> {
    return envelope(() => client.accountExists(email || ''))
  }

  // Primes the server-side OAuth state (SignupFlowsController#prepare_oauth) before the
  // popup navigates to Google. Session-native lane (see PublicApiClient#postSession) — not
  // an /api/v1 call, but still routed through the shared `envelope` helper (loading/error
  // refs + never-throws contract) like every other method here; the popup orchestrator
  // only needs the boolean `ok` flag out of the resulting HttpResult.
  async function primeOAuth(payload: Record<string, unknown>): Promise<boolean> {
    // Drop null/undefined fields before posting: flows without an address step (management/
    // notebook) would otherwise send `address: null`, which the backend's `address: {}` permit
    // rejects with a noisy "Unpermitted parameter: :address" log line.
    const clean = Object.fromEntries(Object.entries(payload).filter(([, v]) => v != null))
    const result = await envelope(() => client.postSession('/signup/chatbot/prepare_oauth', clean))
    return result.ok
  }

  return {
    loading,
    error,
    createAccount,
    createOrganisationAccount,
    fetchAvailability,
    checkEmailExists,
    primeOAuth,
  }
}
